import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Button from '@/components/CustomButtons/Button';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import {
  MAX_REPORT_SFTP_DESTINATIONS,
  emptyReportSftpDestination,
} from './reportSftpDestinations';

export default function ReportSftpDestinationList({
  destinations,
  sftpConnections,
  defaultFileName,
  onChange,
  fieldErrors,
}) {
  const { t } = useTranslation();

  const update = (index, field, value) =>
    onChange(
      destinations.map((destination, i) =>
        i === index ? { ...destination, [field]: value } : destination,
      ),
      `sftp_destinations[${index}].${field}`,
    );

  const remove = (index) =>
    onChange(
      destinations.filter((_, i) => i !== index),
      'sftp_destinations',
    );

  const add = () =>
    onChange(
      [...destinations, emptyReportSftpDestination()],
      'sftp_destinations',
    );

  const errorFor = (index, field) =>
    fieldErrors[`sftp_destinations[${index}].${field}`];

  return (
    <>
      <FormHelperText>
        {t('reporting.form.sftpDestinations.description')}
      </FormHelperText>
      {fieldErrors.sftp_destinations && (
        <FormHelperText error>{fieldErrors.sftp_destinations}</FormHelperText>
      )}

      {destinations.map((destination, index) => (
        <GridContainer
          key={index}
          alignItems='center'
          role='group'
          aria-label={t('reporting.form.sftpDestinations.rowLabel', {
            number: index + 1,
          })}>
          <GridItem xs={12} sm={12} md={4}>
            <div className='custom-select'>
              <FormControl
                variant='standard'
                fullWidth
                error={!!errorFor(index, 'client_sftp_connection_id')}>
                <InputLabel id={`report-sftp-connection-${index}`}>
                  {t('reporting.form.sftpConnection')}
                </InputLabel>
                <Select
                  labelId={`report-sftp-connection-${index}`}
                  value={destination.client_sftp_connection_id}
                  onChange={(e) =>
                    update(index, 'client_sftp_connection_id', e.target.value)
                  }>
                  {sftpConnections.map((connection) => (
                    <MenuItem key={connection.id} value={connection.id}>
                      {connection.name || connection.host}
                    </MenuItem>
                  ))}
                </Select>
                {errorFor(index, 'client_sftp_connection_id') && (
                  <FormHelperText>
                    {errorFor(index, 'client_sftp_connection_id')}
                  </FormHelperText>
                )}
              </FormControl>
            </div>
          </GridItem>
          <GridItem xs={12} sm={12} md={4}>
            <CustomInput
              labelText={t('reporting.form.sftpDestinations.folder')}
              value={destination.outbound_file_location}
              onChange={(e) =>
                update(index, 'outbound_file_location', e.target.value)
              }
              formControlProps={{ fullWidth: true }}
              error={!!errorFor(index, 'outbound_file_location')}
              inputProps={{
                helperText:
                  errorFor(index, 'outbound_file_location') ||
                  t('reporting.form.sftpDestinations.folderHelper'),
              }}
            />
          </GridItem>
          <GridItem xs={10} sm={10} md={3}>
            <CustomInput
              labelText={t('reporting.form.sftpDestinations.fileName')}
              value={destination.file_name}
              onChange={(e) => update(index, 'file_name', e.target.value)}
              formControlProps={{ fullWidth: true }}
              error={!!errorFor(index, 'file_name')}
              inputProps={{
                helperText:
                  errorFor(index, 'file_name') ||
                  t('reporting.form.sftpDestinations.fileNameHelper', {
                    fileName: defaultFileName,
                  }),
              }}
            />
          </GridItem>
          <GridItem xs={2} sm={2} md={1}>
            <Tooltip title={t('reporting.form.sftpDestinations.remove')}>
              <IconButton size='small' onClick={() => remove(index)}>
                <DeleteOutlineIcon
                  fontSize='small'
                  style={{ color: '#f44336' }}
                />
              </IconButton>
            </Tooltip>
          </GridItem>
        </GridContainer>
      ))}

      <Button
        size='sm'
        color='primary'
        simple
        onClick={add}
        disabled={destinations.length >= MAX_REPORT_SFTP_DESTINATIONS}>
        <AddCircleOutlineIcon fontSize='small' />
        {t('reporting.form.sftpDestinations.add')}
      </Button>
    </>
  );
}

ReportSftpDestinationList.propTypes = {
  destinations: PropTypes.array.isRequired,
  sftpConnections: PropTypes.array.isRequired,
  defaultFileName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object.isRequired,
};
